import { useEffect, useRef, useState, useCallback } from 'react';
import { motion } from 'motion/react';
import { App as CapacitorApp } from '@capacitor/app';
import {
  ArrowLeft,
  Settings2,
  Camera,
  Video,
  RotateCcw,
  Sun,
  AlertCircle,
} from 'lucide-react';
import type { Model3D, UserModel } from '@visionar/shared';
import { useUIStore } from '@/stores/ui-store';
import { useCapturesStore } from '@/stores/captures-store';
import { useNotificationsStore } from '@/stores/notifications-store';
import Button from '@/components/ui/Button';

// Union type for models that can be viewed in AR
type ARViewableModel = Model3D | UserModel;

// Type guard to check if model is a catalog Model3D (has brand property)
function isCatalogModel(model: ARViewableModel): model is Model3D {
  return 'brand' in model || 'brandId' in model;
}

interface ARCameraProps {
  model: ARViewableModel | null;
}

// Dynamic script loader for 8th Wall
let xr8LoadPromise: Promise<void> | null = null;

const loadXR8Scripts = async (): Promise<void> => {
  // Return existing promise if already loading
  if (xr8LoadPromise) return xr8LoadPromise;

  // Already loaded
  if (typeof XR8 !== 'undefined') return Promise.resolve();

  xr8LoadPromise = new Promise<void>(async (resolve, reject) => {
    const loadScript = (src: string): Promise<void> => {
      return new Promise((res, rej) => {
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) {
          res();
          return;
        }

        const script = document.createElement('script');
        script.src = src;
        script.async = false;
        script.onload = () => res();
        script.onerror = () => rej(new Error(`Failed to load ${src}`));
        document.head.appendChild(script);
      });
    };

    try {
      console.log('[AR] Loading 8th Wall scripts...');
      await loadScript('/xr.js');
      await loadScript('/xr-slam.js');

      // Wait for XR8 to be available
      let attempts = 0;
      while (typeof XR8 === 'undefined' && attempts < 100) {
        await new Promise(r => setTimeout(r, 50));
        attempts++;
      }

      if (typeof XR8 === 'undefined') {
        reject(new Error('XR8 failed to initialize'));
        return;
      }

      console.log('[AR] 8th Wall loaded successfully');
      resolve();
    } catch (err) {
      reject(err);
    }
  });

  return xr8LoadPromise;
};

// Check if AR libraries are loaded
const isARAvailable = () => {
  return typeof window !== 'undefined' &&
         'XR8' in window &&
         'BABYLON' in window;
};

// Check if BabylonJS is loaded (it loads via CDN in index.html)
const isBabylonLoaded = () => {
  return typeof window !== 'undefined' && 'BABYLON' in window;
};

export default function ARCamera({ model }: ARCameraProps) {
  const { setARActive, showToast } = useUIStore();
  const { addCapture } = useCapturesStore();
  const { notifyCaptureSaved } = useNotificationsStore();

  // Canvas refs
  const cameraCanvasRef = useRef<HTMLCanvasElement>(null);
  const babylonCanvasRef = useRef<HTMLCanvasElement>(null);

  // AR Engine state
  const engineRef = useRef<unknown>(null);
  const sceneRef = useRef<unknown>(null);
  const cameraRef = useRef<unknown>(null);
  const modelRef = useRef<unknown>(null);
  const shadowGenRef = useRef<unknown>(null);
  const dirLightRef = useRef<unknown>(null);
  const hemiLightRef = useRef<unknown>(null);

  // UI State
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaced, setIsPlaced] = useState(false);
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const [scale, setScale] = useState(100);
  const [lightInfo, setLightInfo] = useState({ intensity: 1, temperature: 6500 });
  const [error, setError] = useState<string | null>(null);
  const [isARSupported, setIsARSupported] = useState(true);
  const [isRecording, setIsRecording] = useState(false);

  // Video recording refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const isRecordingRef = useRef(false);

  // Touch state for gestures
  const touchStateRef = useRef({
    lastX: 0,
    lastY: 0,
    initialPinchDist: 0,
    initialPinchAngle: 0,
    initialScale: 1,
    initialRotation: 0,
  });

  // Get display info based on model type
  const getDisplayInfo = () => {
    if (!model) return { name: 'Select a model', brandName: '' };
    if (isCatalogModel(model)) {
      return {
        name: model.name,
        brandName: typeof model.brand === 'object' ? model.brand?.name : '',
      };
    }
    // UserModel - no brand, just name
    return { name: model.name, brandName: 'My Model' };
  };
  const displayInfo = getDisplayInfo();

  // Initialize BabylonJS Engine
  const initBabylon = useCallback(async () => {
    if (!babylonCanvasRef.current || !isARAvailable()) return null;

    const BABYLON = (window as unknown as { BABYLON: typeof import('babylonjs') }).BABYLON;

    // Try WebGPU first
    let engine: unknown;
    const webGPUSupported = await BABYLON.WebGPUEngine.IsSupportedAsync;

    if (webGPUSupported) {
      const webgpuEngine = new BABYLON.WebGPUEngine(babylonCanvasRef.current, {
        antialiasing: true,
        stencil: true,
        adaptToDeviceRatio: true,
        alphaMode: BABYLON.Constants.ALPHA_PREMULTIPLIED,
      });
      await webgpuEngine.initAsync();
      engine = webgpuEngine;
      console.log('[AR] Using WebGPU engine');
    } else {
      engine = new BABYLON.Engine(babylonCanvasRef.current, true, {
        preserveDrawingBuffer: true,
        stencil: true,
        alpha: true,
        premultipliedAlpha: false,
        powerPreference: 'high-performance',
      });
      console.log('[AR] Using WebGL engine');
    }

    engineRef.current = engine;

    // Create scene
    const scene = new BABYLON.Scene(engine as BABYLON.Engine);
    scene.clearColor = new BABYLON.Color4(0, 0, 0, 0);
    sceneRef.current = scene;

    // Configure Draco decoder
    BABYLON.DracoCompression.Configuration = {
      decoder: {
        wasmUrl: 'https://cdn.babylonjs.com/draco_wasm_wrapper_gltf.js',
        wasmBinaryUrl: 'https://cdn.babylonjs.com/draco_decoder_gltf.wasm',
        fallbackUrl: 'https://cdn.babylonjs.com/draco_decoder_gltf.js',
      },
    };

    // Environment
    scene.environmentTexture = BABYLON.CubeTexture.CreateFromPrefilteredData(
      'https://playground.babylonjs.com/textures/environment.env',
      scene
    );
    scene.environmentIntensity = 0.8;

    // Image processing
    scene.imageProcessingConfiguration.exposure = 1.0;
    scene.imageProcessingConfiguration.contrast = 1.1;
    scene.imageProcessingConfiguration.toneMappingEnabled = true;
    scene.imageProcessingConfiguration.toneMappingType = BABYLON.ImageProcessingConfiguration.TONEMAPPING_ACES;

    // Camera (synced by XR8)
    const camera = new BABYLON.FreeCamera('cam', new BABYLON.Vector3(0, 1.6, 0), scene);
    camera.minZ = 0.01;
    camera.maxZ = 1000;
    camera.rotationQuaternion = new BABYLON.Quaternion();
    cameraRef.current = camera;

    // Lights
    const hemiLight = new BABYLON.HemisphericLight('hemi', new BABYLON.Vector3(0, 1, 0), scene);
    hemiLight.intensity = 0.4;
    hemiLight.groundColor = new BABYLON.Color3(0.2, 0.2, 0.25);
    hemiLightRef.current = hemiLight;

    const dirLight = new BABYLON.DirectionalLight('dir', new BABYLON.Vector3(-0.5, -1, 0.5), scene);
    dirLight.position = new BABYLON.Vector3(10, 20, 10);
    dirLight.intensity = 0.8;
    dirLightRef.current = dirLight;

    // Shadows
    const shadowGenerator = new BABYLON.ShadowGenerator(1024, dirLight);
    shadowGenerator.useBlurExponentialShadowMap = true;
    shadowGenerator.blurKernel = 32;
    shadowGenerator.darkness = 0.4;
    shadowGenerator.bias = 0.001;
    shadowGenerator.normalBias = 0.02;
    shadowGenRef.current = shadowGenerator;

    // Shadow ground
    const ground = BABYLON.MeshBuilder.CreateGround('ground', { width: 100, height: 100 }, scene);
    const shadowMat = new BABYLON.ShadowOnlyMaterial('shadowMat', scene);
    shadowMat.activeLight = dirLight;
    shadowMat.alpha = 0.4;
    ground.material = shadowMat;
    ground.receiveShadows = true;

    // Render loop
    (engine as BABYLON.Engine).runRenderLoop(() => {
      scene.render();
    });

    window.addEventListener('resize', () => (engine as BABYLON.Engine).resize());

    return { engine, scene, camera };
  }, []);

  // Initialize XR8
  const initXR8 = useCallback(async () => {
    if (!cameraCanvasRef.current || !isARAvailable()) return;

    const XR8 = (window as unknown as { XR8: typeof globalThis.XR8 }).XR8;
    const canvas = cameraCanvasRef.current;

    // DPR-aware sizing
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = window.innerWidth * dpr;
    canvas.height = window.innerHeight * dpr;

    // Sync BabylonJS canvas to match camera canvas
    const babylonCanvas = babylonCanvasRef.current;
    const engine = engineRef.current as BABYLON.Engine | null;
    if (babylonCanvas) {
      babylonCanvas.width = canvas.width;
      babylonCanvas.height = canvas.height;
      engine?.resize();
    }

    // Load SLAM
    await XR8.loadChunk('slam');

    // Camera sync module with light estimation
    const cameraSyncModule = {
      name: 'babylon-sync',
      onStart: () => {
        console.log('[AR] XR8 ready');
        setIsLoading(false);
      },
      onUpdate: ({ processCpuResult }: { processCpuResult?: { reality?: {
        rotation?: { x: number; y: number; z: number; w: number };
        position?: { x: number; y: number; z: number };
        lighting?: { exposure?: number; temperature?: number };
      }}}) => {
        const camera = cameraRef.current as BABYLON.FreeCamera | null;
        if (!camera || !processCpuResult?.reality) return;

        const { rotation, position, lighting } = processCpuResult.reality;
        const BABYLON = (window as unknown as { BABYLON: typeof import('babylonjs') }).BABYLON;

        // Sync camera
        if (rotation && camera.rotationQuaternion) {
          camera.rotationQuaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
        }

        if (position) {
          camera.position.set(position.x, position.y, position.z);
        }

        // Light estimation
        if (lighting) {
          const intensity = lighting.exposure ?? 1.0;
          const temperature = lighting.temperature ?? 6500;

          setLightInfo({ intensity, temperature });

          // Apply to scene lights
          const hemi = hemiLightRef.current as BABYLON.HemisphericLight | null;
          const dir = dirLightRef.current as BABYLON.DirectionalLight | null;
          const scene = sceneRef.current as BABYLON.Scene | null;

          if (hemi && dir && scene) {
            hemi.intensity = 0.3 + intensity * 0.4;
            dir.intensity = 0.5 + intensity * 0.5;

            const normalizedTemp = (temperature - 2000) / 8000;
            const warmColor = new BABYLON.Color3(1.0, 0.85, 0.7);
            const coolColor = new BABYLON.Color3(0.8, 0.9, 1.0);
            const lightColor = BABYLON.Color3.Lerp(warmColor, coolColor, normalizedTemp);

            dir.diffuse = lightColor;
            hemi.diffuse = lightColor;
            scene.environmentIntensity = 0.5 + intensity * 0.5;
          }
        }
      },
    };

    // Configure XR8
    XR8.XrController?.configure({
      enableLighting: true,
      leftHandedAxes: true,
    });

    const modules: unknown[] = [XR8.GlTextureRenderer.pipelineModule()];

    if (XR8.XrController?.pipelineModule) {
      modules.push(XR8.XrController.pipelineModule());
    }

    modules.push(cameraSyncModule);

    XR8.addCameraPipelineModules(modules);

    XR8.run({
      canvas,
      allowedDevices: XR8.XrConfig?.device()?.ANY || 'any',
      cameraConfig: {
        direction: XR8.XrConfig?.camera()?.BACK || 'back',
      },
    });

    // Resize handler - sync both canvases
    window.addEventListener('resize', () => {
      const newDpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * newDpr;
      canvas.height = window.innerHeight * newDpr;

      // Also resize BabylonJS canvas and engine
      const babylonCanvas = babylonCanvasRef.current;
      const engine = engineRef.current as BABYLON.Engine | null;
      if (babylonCanvas && engine) {
        babylonCanvas.width = canvas.width;
        babylonCanvas.height = canvas.height;
        engine.resize();
      }
    });
  }, []);

  // Base scale for normalization (stored after model load)
  const baseScaleRef = useRef(0.5);

  // Load 3D model
  const loadModel = useCallback(async (modelUrl: string) => {
    const scene = sceneRef.current as BABYLON.Scene | null;
    const shadowGen = shadowGenRef.current as BABYLON.ShadowGenerator | null;
    if (!scene || !isARAvailable()) return;

    const BABYLON = (window as unknown as { BABYLON: typeof import('babylonjs') }).BABYLON;

    try {
      const result = await BABYLON.SceneLoader.ImportMeshAsync('', modelUrl, '', scene);

      const loadedModel = result.meshes[0];
      loadedModel.setEnabled(false);

      // Calculate bounding box to auto-scale model
      // Target size: ~2 meters for the largest dimension
      const TARGET_SIZE = 2.0;
      loadedModel.scaling = new BABYLON.Vector3(1, 1, 1); // Reset to measure true size
      loadedModel.computeWorldMatrix(true);

      const childMeshes = loadedModel.getChildMeshes();
      if (childMeshes.length > 0) {
        let minBounds = new BABYLON.Vector3(Infinity, Infinity, Infinity);
        let maxBounds = new BABYLON.Vector3(-Infinity, -Infinity, -Infinity);

        childMeshes.forEach((mesh) => {
          mesh.computeWorldMatrix(true);
          const boundingInfo = mesh.getBoundingInfo();
          const min = boundingInfo.boundingBox.minimumWorld;
          const max = boundingInfo.boundingBox.maximumWorld;

          minBounds = BABYLON.Vector3.Minimize(minBounds, min);
          maxBounds = BABYLON.Vector3.Maximize(maxBounds, max);
        });

        const size = maxBounds.subtract(minBounds);
        const largestDimension = Math.max(size.x, size.y, size.z);

        if (largestDimension > 0) {
          // Calculate scale to fit target size
          baseScaleRef.current = TARGET_SIZE / largestDimension;
          console.log(`[AR] Model auto-scale: ${baseScaleRef.current.toFixed(3)} (original size: ${largestDimension.toFixed(2)}m)`);
        }
      }

      // Setup shadows and PBR
      result.meshes.forEach((mesh) => {
        if (mesh.name !== '__root__' && shadowGen) {
          shadowGen.addShadowCaster(mesh);
          mesh.receiveShadows = true;
        }

        if (mesh.material && (mesh.material as BABYLON.PBRMaterial).albedoColor) {
          const pbr = mesh.material as BABYLON.PBRMaterial;
          pbr.useRadianceOcclusion = true;
          pbr.useHorizonOcclusion = true;
          pbr.environmentIntensity = 1.0;
        }
      });

      modelRef.current = loadedModel;
      console.log('[AR] Model loaded');
    } catch (err) {
      console.error('[AR] Model load error:', err);
      setError('Failed to load 3D model');
    }
  }, []);

  // Place model in front of camera
  const placeModel = useCallback(() => {
    const camera = cameraRef.current as BABYLON.FreeCamera | null;
    const loadedModel = modelRef.current as BABYLON.TransformNode | null;
    const scene = sceneRef.current as BABYLON.Scene | null;
    const shadowGen = shadowGenRef.current as BABYLON.ShadowGenerator | null;

    if (!camera || !loadedModel || !scene || !isARAvailable()) return;

    const BABYLON = (window as unknown as { BABYLON: typeof import('babylonjs') }).BABYLON;

    // Spawn in front of camera
    const distance = 4;
    const forward = camera.getDirection(BABYLON.Vector3.Forward());
    const camPos = camera.position;

    const x = camPos.x + forward.x * distance;
    const z = camPos.z + forward.z * distance;

    loadedModel.setEnabled(true);

    // Use auto-calculated base scale for proper sizing
    const modelScale = (scale / 100) * baseScaleRef.current;
    loadedModel.scaling = new BABYLON.Vector3(modelScale, modelScale, modelScale);

    // Calculate model height to place on ground properly
    loadedModel.computeWorldMatrix(true);
    const childMeshes = loadedModel.getChildMeshes();
    let minY = 0;
    if (childMeshes.length > 0) {
      childMeshes.forEach((mesh) => {
        mesh.computeWorldMatrix(true);
        const boundingInfo = mesh.getBoundingInfo();
        const meshMinY = boundingInfo.boundingBox.minimumWorld.y;
        if (meshMinY < minY) minY = meshMinY;
      });
    }
    // Position model so its bottom touches the ground (y=0)
    const groundY = -minY;
    loadedModel.position = new BABYLON.Vector3(x, groundY, z);

    // Face camera
    const angle = Math.atan2(camPos.x - x, camPos.z - z);
    loadedModel.rotation = new BABYLON.Vector3(0, angle, 0);

    // Add shadows
    loadedModel.getChildMeshes().forEach((mesh) => {
      if (shadowGen) {
        shadowGen.addShadowCaster(mesh);
        mesh.receiveShadows = true;
      }
    });

    // Spawn animation - start small and grow to target scale
    const startScale = modelScale * 0.1;
    loadedModel.scaling = new BABYLON.Vector3(startScale, startScale, startScale);
    const spawnAnim = new BABYLON.Animation(
      'spawn',
      'scaling',
      60,
      BABYLON.Animation.ANIMATIONTYPE_VECTOR3,
      BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
    );

    const easeFunc = new BABYLON.BackEase(0.3);
    easeFunc.setEasingMode(BABYLON.EasingFunction.EASINGMODE_EASEOUT);

    spawnAnim.setKeys([
      { frame: 0, value: new BABYLON.Vector3(startScale, startScale, startScale) },
      { frame: 40, value: new BABYLON.Vector3(modelScale, modelScale, modelScale) },
    ]);
    spawnAnim.setEasingFunction(easeFunc);

    loadedModel.animations = [spawnAnim];
    scene.beginAnimation(loadedModel, 0, 40, false);

    setIsPlaced(true);
    showToast('Model placed! Drag to move, pinch to scale.', 'success');
  }, [scale, showToast]);

  // Change model color
  const changeColor = useCallback((hexColor: string) => {
    const loadedModel = modelRef.current as BABYLON.TransformNode | null;
    if (!loadedModel || !isARAvailable()) return;

    const BABYLON = (window as unknown as { BABYLON: typeof import('babylonjs') }).BABYLON;

    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    const color = new BABYLON.Color3(r, g, b);

    loadedModel.getChildMeshes().forEach((mesh) => {
      const name = mesh.name.toLowerCase();
      if (
        name.includes('body') ||
        name.includes('hood') ||
        name.includes('door') ||
        name.includes('fender') ||
        name.includes('bumper') ||
        name.includes('roof')
      ) {
        if (mesh.material && (mesh.material as BABYLON.PBRMaterial).albedoColor) {
          (mesh.material as BABYLON.PBRMaterial).albedoColor = color;
        }
      }
    });
  }, []);

  // Update scale (uses auto-calculated base scale)
  const updateScale = useCallback((newScale: number) => {
    setScale(newScale);
    const loadedModel = modelRef.current as BABYLON.TransformNode | null;
    if (!loadedModel || !isARAvailable()) return;

    const BABYLON = (window as unknown as { BABYLON: typeof import('babylonjs') }).BABYLON;
    const modelScale = (newScale / 100) * baseScaleRef.current;
    loadedModel.scaling = new BABYLON.Vector3(modelScale, modelScale, modelScale);
  }, []);

  // Take screenshot
  const takeScreenshot = useCallback(async () => {
    const cameraCanvas = cameraCanvasRef.current;
    const babylonCanvas = babylonCanvasRef.current;
    const scene = sceneRef.current as BABYLON.Scene | null;
    const engine = engineRef.current as BABYLON.Engine | null;
    const camera = cameraRef.current as BABYLON.FreeCamera | null;

    if (!cameraCanvas || !babylonCanvas || !scene || !engine || !camera) return;

    const BABYLON = (window as unknown as { BABYLON: typeof import('babylonjs') }).BABYLON;

    // Flash effect first
    const flash = document.createElement('div');
    flash.className = 'fixed inset-0 bg-white z-50 pointer-events-none';
    document.body.appendChild(flash);
    setTimeout(() => flash.remove(), 100);

    try {
      // Use BabylonJS screenshot API which works with both WebGL and WebGPU
      const screenshotPromise = new Promise<string>((resolve) => {
        BABYLON.Tools.CreateScreenshotUsingRenderTarget(
          engine,
          camera,
          { width: cameraCanvas.width, height: cameraCanvas.height },
          (data) => resolve(data),
          'image/png',
          1, // samples
          false, // antialiasing
          undefined, // fileName
          undefined, // renderSprites
          false // enableStencilBuffer
        );
      });

      const babylonScreenshot = await screenshotPromise;

      // Create composite canvas
      const composite = document.createElement('canvas');
      composite.width = cameraCanvas.width;
      composite.height = cameraCanvas.height;
      const ctx = composite.getContext('2d');

      if (ctx) {
        // Draw camera feed first
        ctx.drawImage(cameraCanvas, 0, 0);

        // Load and draw BabylonJS screenshot
        const babylonImg = new Image();
        await new Promise<void>((resolve, reject) => {
          babylonImg.onload = () => resolve();
          babylonImg.onerror = reject;
          babylonImg.src = babylonScreenshot;
        });
        ctx.drawImage(babylonImg, 0, 0, cameraCanvas.width, cameraCanvas.height);
      }

      const dataUrl = composite.toDataURL('image/jpeg', 0.92);

      // Save to captures store
      addCapture({
        type: 'photo',
        dataUrl,
        modelId: model?.id,
        modelName: model?.name,
      });

      // Notify user
      notifyCaptureSaved('photo', model?.name);
      showToast('Photo captured!', 'success');

      // Try to share
      if ('share' in navigator && 'canShare' in navigator) {
        try {
          const response = await fetch(dataUrl);
          const blob = await response.blob();
          const file = new File([blob], 'visionar-capture.jpg', { type: 'image/jpeg' });

          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: 'VisionAR Capture',
              files: [file],
            });
          }
        } catch (err) {
          if ((err as Error).name !== 'AbortError') {
            console.error('Share failed:', err);
          }
        }
      }
    } catch (err) {
      console.error('[AR] Screenshot failed:', err);
      showToast('Failed to capture screenshot', 'error');
    }
  }, [showToast, addCapture, notifyCaptureSaved, model]);

  // Start/Stop video recording
  const toggleRecording = useCallback(async () => {
    const cameraCanvas = cameraCanvasRef.current;
    const babylonCanvas = babylonCanvasRef.current;

    if (!cameraCanvas || !babylonCanvas) return;

    if (isRecordingRef.current) {
      // Stop recording
      isRecordingRef.current = false;
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      setIsRecording(false);
      return;
    }

    // Start recording
    try {
      // Create composite canvas for recording
      const compositeCanvas = document.createElement('canvas');
      compositeCanvas.width = cameraCanvas.width;
      compositeCanvas.height = cameraCanvas.height;
      const ctx = compositeCanvas.getContext('2d');

      if (!ctx) {
        showToast('Failed to start recording', 'error');
        return;
      }

      recordedChunksRef.current = [];

      // Capture stream from composite canvas
      const stream = compositeCanvas.captureStream(30);

      // Add audio if available
      try {
        const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioStream.getAudioTracks().forEach(track => stream.addTrack(track));
      } catch {
        console.log('[AR] Recording without audio');
      }

      // Determine best mime type
      let mimeType = 'video/webm;codecs=vp9';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm;codecs=vp8';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm';
        }
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType });

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          recordedChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        const dataUrl = URL.createObjectURL(blob);

        // Save to captures store
        addCapture({
          type: 'video',
          dataUrl,
          modelId: model?.id,
          modelName: model?.name,
        });

        notifyCaptureSaved('video', model?.name);
        showToast('Video saved!', 'success');

        // Try to share
        if ('share' in navigator && 'canShare' in navigator) {
          try {
            const file = new File([blob], 'visionar-recording.webm', { type: 'video/webm' });
            if (navigator.canShare({ files: [file] })) {
              await navigator.share({
                title: 'VisionAR Recording',
                files: [file],
              });
            }
          } catch (err) {
            if ((err as Error).name !== 'AbortError') {
              console.error('Share failed:', err);
            }
          }
        }
      };

      // Start compositing frames using ref for tracking
      const drawFrame = () => {
        if (!isRecordingRef.current) return;
        ctx.drawImage(cameraCanvas, 0, 0);
        ctx.drawImage(babylonCanvas, 0, 0);
        requestAnimationFrame(drawFrame);
      };

      mediaRecorderRef.current = mediaRecorder;
      isRecordingRef.current = true;
      setIsRecording(true);
      mediaRecorder.start(100); // Collect data every 100ms
      drawFrame();

      showToast('Recording started...', 'info');
    } catch (err) {
      console.error('[AR] Recording failed:', err);
      showToast('Failed to start recording', 'error');
    }
  }, [showToast, addCapture, notifyCaptureSaved, model]);

  // Reset
  const handleReset = useCallback(() => {
    const loadedModel = modelRef.current as BABYLON.TransformNode | null;
    if (loadedModel) {
      loadedModel.setEnabled(false);
    }
    setIsPlaced(false);
    setScale(100);
    showToast('Model reset', 'info');
  }, [showToast]);

  // Close AR
  const handleClose = useCallback(() => {
    // Cleanup
    const XR8 = (window as unknown as { XR8?: { stop?: () => void } }).XR8;
    XR8?.stop?.();

    const engine = engineRef.current as BABYLON.Engine | null;
    engine?.dispose();

    setARActive(false);
  }, [setARActive]);

  // Touch handlers for gestures
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!isPlaced) return;
    e.preventDefault();

    const touch = touchStateRef.current;
    const loadedModel = modelRef.current as BABYLON.TransformNode | null;

    if (e.touches.length === 1) {
      touch.lastX = e.touches[0].clientX;
      touch.lastY = e.touches[0].clientY;
    } else if (e.touches.length === 2 && loadedModel) {
      const dx = e.touches[1].clientX - e.touches[0].clientX;
      const dy = e.touches[1].clientY - e.touches[0].clientY;
      touch.initialPinchDist = Math.sqrt(dx * dx + dy * dy);
      touch.initialPinchAngle = Math.atan2(dy, dx);
      touch.initialScale = loadedModel.scaling.x;
      touch.initialRotation = loadedModel.rotation.y;
    }
  }, [isPlaced]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPlaced || !isARAvailable()) return;
    e.preventDefault();

    const touch = touchStateRef.current;
    const loadedModel = modelRef.current as BABYLON.TransformNode | null;
    const BABYLON = (window as unknown as { BABYLON: typeof import('babylonjs') }).BABYLON;

    if (!loadedModel) return;

    if (e.touches.length === 1) {
      const deltaX = e.touches[0].clientX - touch.lastX;
      const deltaY = e.touches[0].clientY - touch.lastY;

      loadedModel.position.x += deltaX * 0.01;
      loadedModel.position.z -= deltaY * 0.01;

      touch.lastX = e.touches[0].clientX;
      touch.lastY = e.touches[0].clientY;
    } else if (e.touches.length === 2) {
      const dx = e.touches[1].clientX - e.touches[0].clientX;
      const dy = e.touches[1].clientY - e.touches[0].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const angle = Math.atan2(dy, dx);

      // Scale (clamped to same range as original: 0.15 - 2.5)
      const scaleRatio = dist / touch.initialPinchDist;
      let newScale = touch.initialScale * scaleRatio;
      newScale = Math.max(0.15, Math.min(2.5, newScale));
      loadedModel.scaling = new BABYLON.Vector3(newScale, newScale, newScale);
      setScale(Math.round((newScale / baseScaleRef.current) * 100));

      // Rotate (using stored initialPinchAngle from touchstart)
      loadedModel.rotation.y = touch.initialRotation + (angle - touch.initialPinchAngle);
    }
  }, [isPlaced]);

  // Initialize AR
  useEffect(() => {
    const init = async () => {
      try {
        // Check if BabylonJS is loaded (from CDN)
        if (!isBabylonLoaded()) {
          console.error('[AR] BabylonJS not loaded');
          setError('BabylonJS library not loaded');
          setIsARSupported(false);
          setIsLoading(false);
          return;
        }

        // Load 8th Wall scripts dynamically
        console.log('[AR] Loading 8th Wall...');
        await loadXR8Scripts();

        // Now check if AR is available
        if (!isARAvailable()) {
          console.error('[AR] AR not available after loading scripts');
          setIsARSupported(false);
          setIsLoading(false);
          return;
        }

        console.log('[AR] Initializing BabylonJS...');
        await initBabylon();

        console.log('[AR] Initializing XR8...');
        await initXR8();

        // Load model if provided
        if (model?.modelUrl) {
          console.log('[AR] Loading model:', model.modelUrl);
          await loadModel(model.modelUrl);
        }
      } catch (err) {
        console.error('[AR] Init error:', err);
        setError(`Failed to initialize AR: ${(err as Error).message}`);
        setIsLoading(false);
      }
    };

    init();

    return () => {
      const XR8 = (window as unknown as { XR8?: { stop?: () => void } }).XR8;
      XR8?.stop?.();
      const engine = engineRef.current as BABYLON.Engine | null;
      engine?.dispose();
    };
  }, [initBabylon, initXR8, loadModel, model]);

  // Handle back button to close AR
  useEffect(() => {
    const backButtonListener = CapacitorApp.addListener('backButton', () => {
      handleClose();
    });

    return () => {
      backButtonListener.then(l => l.remove());
    };
  }, [handleClose]);

  // Handle app lifecycle (pause/resume when app goes to background)
  useEffect(() => {
    let wasInAR = false;

    const handleAppStateChange = async ({ isActive }: { isActive: boolean }) => {
      const XR8 = (window as unknown as { XR8?: {
        pause?: () => void;
        resume?: () => void;
        isPaused?: () => boolean;
        stop?: () => void;
        run?: (config: unknown) => void;
      } }).XR8;

      const engine = engineRef.current as BABYLON.Engine | null;

      if (isActive) {
        console.log('[AR] App resumed');

        // Resume BabylonJS rendering
        if (engine) {
          engine.runRenderLoop(() => {
            const scene = sceneRef.current as BABYLON.Scene | null;
            scene?.render();
          });
        }

        // Resume XR8
        if (XR8 && wasInAR) {
          try {
            // Try the standard resume method
            if (typeof XR8.resume === 'function') {
              XR8.resume();
              console.log('[AR] XR8 resumed');
            } else if (cameraCanvasRef.current) {
              // Fallback: restart XR8 completely
              console.log('[AR] Restarting XR8...');
              const canvas = cameraCanvasRef.current;
              const dpr = Math.min(window.devicePixelRatio || 1, 2);
              canvas.width = window.innerWidth * dpr;
              canvas.height = window.innerHeight * dpr;

              // Sync BabylonJS canvas
              const babylonCanvas = babylonCanvasRef.current;
              if (babylonCanvas) {
                babylonCanvas.width = canvas.width;
                babylonCanvas.height = canvas.height;
              }

              XR8.run?.({
                canvas,
                allowedDevices: 'any',
                cameraConfig: { direction: 'back' },
              });
            }
          } catch (err) {
            console.error('[AR] Failed to resume:', err);
            setError('AR session was interrupted. Please try again.');
          }
        }
      } else {
        console.log('[AR] App paused');
        wasInAR = !!XR8;

        // Pause XR8
        if (XR8) {
          try {
            if (typeof XR8.pause === 'function') {
              XR8.pause();
              console.log('[AR] XR8 paused');
            }
          } catch (err) {
            console.error('[AR] Failed to pause:', err);
          }
        }

        // Stop BabylonJS rendering to save battery
        if (engine) {
          engine.stopRenderLoop();
        }
      }
    };

    // Listen for app state changes
    const listener = CapacitorApp.addListener('appStateChange', handleAppStateChange);

    return () => {
      listener.then(l => l.remove());
    };
  }, []);

  // Handle color change - only for catalog models
  useEffect(() => {
    if (model && isCatalogModel(model) && model.colors && model.colors[selectedColorIndex]) {
      changeColor(model.colors[selectedColorIndex].hexValue);
    }
  }, [selectedColorIndex, model, changeColor]);

  // Error state
  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black z-40 flex flex-col items-center justify-center p-6"
      >
        <AlertCircle size={48} className="text-red-400 mb-4" />
        <h2 className="text-xl font-semibold mb-2">AR Error</h2>
        <p className="text-white/70 text-center mb-6">{error}</p>
        <Button onClick={handleClose}>Go Back</Button>
      </motion.div>
    );
  }

  // AR not supported
  if (!isARSupported) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black z-40 flex flex-col items-center justify-center p-6"
      >
        <AlertCircle size={48} className="text-yellow-400 mb-4" />
        <h2 className="text-xl font-semibold mb-2">AR Not Available</h2>
        <p className="text-white/70 text-center mb-6">
          {error || 'Failed to load AR libraries. Please check your connection and try again.'}
        </p>
        <Button onClick={handleClose}>Go Back</Button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 bg-black z-40 overflow-hidden"
    >
      {/* Camera Canvas (8th Wall renders here) */}
      <canvas
        ref={cameraCanvasRef}
        id="camera-canvas"
        className="absolute inset-0 w-full h-full"
        style={{ touchAction: 'none' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
      />

      {/* BabylonJS 3D Canvas (overlay) */}
      <canvas
        ref={babylonCanvasRef}
        id="babylon-canvas"
        className="absolute inset-0 w-full h-full pointer-events-none"
      />

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center z-30">
          <div className="w-16 h-16 border-4 border-accent-blue/30 border-t-accent-blue rounded-full animate-spin mb-4" />
          <p className="text-white/70">Initializing AR...</p>
          <p className="text-white/50 text-sm mt-1">Please allow camera access</p>
        </div>
      )}

      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 p-4 pt-12 flex justify-between items-center z-20 safe-area-top">
        <button
          onClick={handleClose}
          className="w-12 h-12 rounded-full glass-dark flex items-center justify-center touch-feedback"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="glass-dark rounded-full px-5 py-2.5 flex flex-col items-center">
          <span className="text-[10px] text-white/60 uppercase tracking-widest font-bold">
            {displayInfo.brandName}
          </span>
          <span className="text-sm font-semibold">{displayInfo.name}</span>
        </div>
        <button className="w-12 h-12 rounded-full glass-dark flex items-center justify-center touch-feedback">
          <Settings2 size={24} />
        </button>
      </div>

      {/* Light Estimation Info */}
      <div className="absolute top-28 left-4 glass-dark rounded-xl px-3 py-2 z-20">
        <div className="flex items-center gap-2">
          <Sun size={14} className="text-yellow-400" />
          <span className="text-xs text-white/70">
            {lightInfo.intensity > 0.7 ? 'Bright' : lightInfo.intensity > 0.4 ? 'Normal' : 'Low'}
          </span>
        </div>
      </div>

      {/* Right Side Controls */}
      <div className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col gap-3 z-20">
        <button
          onClick={takeScreenshot}
          disabled={!isPlaced}
          className="w-12 h-12 rounded-full glass-dark flex items-center justify-center touch-feedback disabled:opacity-50"
        >
          <Camera size={20} />
        </button>
        <button
          onClick={toggleRecording}
          disabled={!isPlaced}
          className={`w-12 h-12 rounded-full flex items-center justify-center touch-feedback disabled:opacity-50 ${
            isRecording ? 'bg-red-500 animate-pulse' : 'glass-dark'
          }`}
        >
          <Video size={20} className={isRecording ? 'text-white' : ''} />
        </button>
        <button
          onClick={handleReset}
          disabled={!isPlaced}
          className="w-12 h-12 rounded-full glass-dark flex items-center justify-center touch-feedback disabled:opacity-50"
        >
          <RotateCcw size={20} />
        </button>
      </div>

      {/* Bottom Controls */}
      <div className="absolute bottom-0 left-0 right-0 p-4 pb-8 z-20 safe-area-bottom">
        <div className="glass-dark rounded-[32px] p-6">
          {/* Colors - only for catalog models */}
          {model && isCatalogModel(model) && model.colors && model.colors.length > 0 ? (
            <div className="flex justify-between items-center mb-6">
              <div className="flex gap-3">
                {model.colors.map((color, i) => (
                  <button
                    key={color.id}
                    onClick={() => setSelectedColorIndex(i)}
                    className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                      selectedColorIndex === i ? 'border-white scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color.hexValue }}
                  />
                ))}
              </div>
              <div className="text-right">
                <p className="text-xs text-white/50 mb-0.5">Scale</p>
                <p className="text-sm font-semibold">{scale}%</p>
              </div>
            </div>
          ) : (
            <div className="flex justify-end items-center mb-4">
              <div className="text-right">
                <p className="text-xs text-white/50 mb-0.5">Scale</p>
                <p className="text-sm font-semibold">{scale}%</p>
              </div>
            </div>
          )}

          {/* Scale Slider */}
          {isPlaced && (
            <div className="mb-6">
              <input
                type="range"
                min="50"
                max="200"
                value={scale}
                onChange={(e) => updateScale(Number(e.target.value))}
                className="w-full h-2 bg-white/10 rounded-full appearance-none cursor-pointer
                  [&::-webkit-slider-thumb]:appearance-none
                  [&::-webkit-slider-thumb]:w-5
                  [&::-webkit-slider-thumb]:h-5
                  [&::-webkit-slider-thumb]:bg-white
                  [&::-webkit-slider-thumb]:rounded-full
                  [&::-webkit-slider-thumb]:shadow-lg"
              />
            </div>
          )}

          {/* Place Button */}
          {!isPlaced ? (
            <Button fullWidth size="lg" onClick={placeModel} disabled={isLoading}>
              Place Model
            </Button>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <Button variant="secondary" onClick={handleReset}>
                Reset
              </Button>
              <Button onClick={takeScreenshot}>Capture</Button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
